/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import type { CoinEarnRule, CoinProgramWithStats } from "@lemmonhq/types";
import { EarnRulesEditor } from "./EarnRulesEditor";
import { WalletDesignSelect } from "../../components/WalletDesignSelect";
import { IconPicker } from "./IconPicker";
import {
  RedemptionTiersEditor,
  emptyTierDraft,
  tierToDraft,
  type TierDraft,
} from "./RedemptionTiersEditor";

type CoinProgramFormData = {
  name: string;
  /** Null keeps the plain default design on create. */
  wallet_pass_design_id: string | null;
  coin_type: string;
  coin_label: string;
  icon: string | null;
  earn_rules: CoinEarnRule[];
  max_coins_per_payment: number | null;
  validity_months: number | null;
  enabled: boolean;
};

/** `pizza_points`, never `pizza-points` — the two must not both be reachable. */
const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

const COIN_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;

export type CoinProgramEditorProps = {
  program?: CoinProgramWithStats;
  onClose: () => void;
  onSaved: (program: CoinProgramWithStats) => void;
  scope?: "shop" | "group";
};

export const CoinProgramEditor = defineComponent<CoinProgramEditorProps>({
  name: "CoinProgramEditor",
  props: ["program", "onClose", "onSaved", "scope"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const queryClient = useQueryClient();

    const formData = ref<CoinProgramFormData>({
      name: props.program?.name ?? "",
      wallet_pass_design_id: props.program?.wallet_pass_design_id ?? null,
      coin_type: props.program?.coin_type ?? "",
      coin_label: props.program?.coin_label ?? "",
      icon: props.program?.icon ?? null,
      earn_rules: Array.isArray(props.program?.earn_rules)
        ? props.program.earn_rules
        : [],
      max_coins_per_payment: props.program?.max_coins_per_payment ?? null,
      validity_months: props.program?.validity_months ?? null,
      enabled: props.program?.enabled ?? true,
    });

    // The slug follows the name until the merchant edits it by hand, and is
    // frozen once the program exists — it is denormalised onto every ledger row.
    const coinTypeTouched = ref(Boolean(props.program));

    // The first tier is created with the program, so it is editable here from
    // the start rather than only after saving.
    const firstTier = props.program?.tiers?.[0];
    const tier = ref<TierDraft>(
      firstTier
        ? tierToDraft(firstTier)
        : emptyTierDraft(props.scope === "group"),
    );

    const setName = (name: string) => {
      formData.value.name = name;
      if (!coinTypeTouched.value) formData.value.coin_type = slugify(name);
    };

    const createMutation = useMutation({
      mutationFn: (data: CoinProgramFormData) => {
        const input = {
          ...data,
          // Created in the same transaction as the program.
          tier_amount: tier.value.amount,
          tier_reward: tier.value.reward as any,
        };
        return props.scope === "group"
          ? trpc.coinPrograms.groupCreate.mutate(input)
          : trpc.coinPrograms.create.mutate(input);
      },
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["coinPrograms"] });
        props.onSaved(result as CoinProgramWithStats);
        props.onClose();
      },
    });

    const updateMutation = useMutation({
      mutationFn: async (data: CoinProgramFormData) => {
        const updateInput = {
          id: props.program!.id,
          name: data.name,
          coin_label: data.coin_label,
          icon: data.icon,
          earn_rules: data.earn_rules,
          max_coins_per_payment: data.max_coins_per_payment,
          validity_months: data.validity_months,
          // A program always has a design; it can only switch to another.
          ...(data.wallet_pass_design_id
            ? { wallet_pass_design_id: data.wallet_pass_design_id }
            : {}),
          enabled: data.enabled,
        };
        const saved =
          props.scope === "group"
            ? await trpc.coinPrograms.groupUpdate.mutate(updateInput)
            : await trpc.coinPrograms.update.mutate(updateInput);

        // The tier is a separate row, so it is a separate call. Sequential
        // rather than parallel: both return the serialized program, and the
        // later answer should be the one that wins.
        if (tier.value.id) {
          const tierInput = {
            program_id: props.program!.id,
            tier_id: tier.value.id,
            amount: tier.value.amount,
            reward: tier.value.reward as any,
          };
          return props.scope === "group"
            ? await trpc.coinPrograms.groupTierUpdate.mutate(tierInput)
            : await trpc.coinPrograms.tierUpdate.mutate(tierInput);
        }

        return saved;
      },
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["coinPrograms"] });
        props.onSaved(result as CoinProgramWithStats);
        props.onClose();
      },
    });

    const saving = computed(
      () => createMutation.isPending.value || updateMutation.isPending.value,
    );

    const error = computed(
      () => createMutation.error.value || updateMutation.error.value,
    );

    const coinTypeError = computed(() =>
      formData.value.coin_type &&
      !COIN_TYPE_PATTERN.test(formData.value.coin_type)
        ? t("promotions.coins.field_coin_type_invalid")
        : null,
    );

    const isValid = computed(
      () =>
        formData.value.name.trim().length > 0 &&
        formData.value.coin_label.trim().length > 0 &&
        COIN_TYPE_PATTERN.test(formData.value.coin_type),
    );

    const handleSave = () => {
      if (!isValid.value) return;
      if (props.program) {
        updateMutation.mutate(formData.value);
      } else {
        createMutation.mutate(formData.value);
      }
    };

    /** `null` in these fields means "no limit", so an empty input is meaningful. */
    const nullableNumber = (raw: string) => {
      const parsed = parseInt(raw);
      return raw && Number.isFinite(parsed) ? parsed : null;
    };

    return () => (
      <RightSheet
        {...{
          isOpen: true,
          onClose: props.onClose,
          title: (
            <div class="flex flex-1 items-center gap-3">
              <div class="flex-1 text-2xl text-accent">
                {props.program
                  ? t("promotions.coins.editor_edit_title")
                  : t("promotions.coins.editor_new_title")}
              </div>
              <div class="flex gap-2 pr-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isValid.value || saving.value}
                  class="btn btn-primary btn-sm"
                >
                  {saving.value && (
                    <span class="loading loading-spinner loading-xs mr-1" />
                  )}
                  {props.program
                    ? t("promotions.coins.editor_save")
                    : t("promotions.coins.editor_create")}
                </button>
              </div>
            </div>
          ),
        }}
      >
        <div class="flex-1 overflow-y-auto p-6 space-y-6 max-h-[calc(100vh-120px)]">
          {error.value && (
            <div class="alert alert-error text-sm">
              {(error.value as Error)?.message ??
                t("promotions.coins.editor_error")}
            </div>
          )}

          {/* Basics */}
          <section class="space-y-4">
            <h3 class="text-lg font-semibold text-accent">
              {t("promotions.coins.section_basics")}
            </h3>

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">
                  {t("promotions.coins.field_name")}
                </span>
              </label>
              <input
                type="text"
                value={formData.value.name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                class="input input-bordered focus:input-primary"
                placeholder={t("promotions.coins.field_name_placeholder")}
              />
            </div>

            <WalletDesignSelect
              kind="coin"
              scope={props.scope}
              value={formData.value.wallet_pass_design_id}
              creating={!props.program}
              onChange={(id: string | null) =>
                (formData.value.wallet_pass_design_id = id)
              }
            />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-accent font-medium">
                    {t("promotions.coins.field_coin_type")}
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.value.coin_type}
                  disabled={Boolean(props.program)}
                  onInput={(e) => {
                    coinTypeTouched.value = true;
                    formData.value.coin_type = (
                      e.target as HTMLInputElement
                    ).value;
                  }}
                  class="input input-bordered focus:input-primary font-mono text-sm disabled:opacity-60"
                  placeholder="pizza_points"
                />
                <label class="label">
                  <span
                    class={`label-text-alt ${coinTypeError.value ? "text-error" : "text-base-content/50"}`}
                  >
                    {coinTypeError.value ??
                      (props.program
                        ? t("promotions.coins.field_coin_type_locked")
                        : t("promotions.coins.field_coin_type_hint"))}
                  </span>
                </label>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text text-accent font-medium">
                    {t("promotions.coins.field_coin_label")}
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.value.coin_label}
                  onInput={(e) =>
                    (formData.value.coin_label = (
                      e.target as HTMLInputElement
                    ).value)
                  }
                  class="input input-bordered focus:input-primary"
                  placeholder={t(
                    "promotions.coins.field_coin_label_placeholder",
                  )}
                />
                <label class="label">
                  <span class="label-text-alt text-base-content/50">
                    {t("promotions.coins.field_coin_label_hint")}
                  </span>
                </label>
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">
                  {t("promotions.coins.field_icon")}
                </span>
              </label>
              <IconPicker
                value={formData.value.icon}
                onChange={(icon: string | null) => (formData.value.icon = icon)}
              />
            </div>
          </section>

          <div class="divider my-0" />

          {/* Earn rules */}
          <section class="space-y-3">
            <h3 class="text-lg font-semibold text-accent">
              {t("promotions.coins.section_rules")}
            </h3>
            <EarnRulesEditor
              value={formData.value.earn_rules}
              coinLabel={
                formData.value.coin_label.trim() ||
                t("promotions.coins.coin_label_fallback")
              }
              onChange={(rules: CoinEarnRule[]) =>
                (formData.value.earn_rules = rules)
              }
              allowedRuleTypes={
                props.scope === "group" ? ["spend", "visit"] : undefined
              }
            />
          </section>

          <div class="divider my-0" />

          {/* Redemption tier */}
          <section class="space-y-3">
            <h3 class="text-lg font-semibold text-accent">
              {t("promotions.coins.section_tier")}
            </h3>
            <RedemptionTiersEditor
              value={tier.value}
              coinLabel={
                formData.value.coin_label.trim() ||
                t("promotions.coins.coin_label_fallback")
              }
              icon={formData.value.icon}
              onChange={(next: TierDraft) => (tier.value = next)}
              groupOwned={props.scope === "group"}
            />
          </section>

          <div class="divider my-0" />

          {/* Limits */}
          <section class="space-y-4">
            <h3 class="text-lg font-semibold text-accent">
              {t("promotions.coins.section_limits")}
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-accent font-medium">
                    {t("promotions.coins.field_max_per_payment")}
                  </span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.value.max_coins_per_payment ?? ""}
                  onInput={(e) =>
                    (formData.value.max_coins_per_payment = nullableNumber(
                      (e.target as HTMLInputElement).value,
                    ))
                  }
                  class="input input-bordered focus:input-primary"
                  placeholder={t("promotions.coins.field_uncapped")}
                />
                <label class="label">
                  <span class="label-text-alt text-base-content/50">
                    {t("promotions.coins.field_max_per_payment_hint")}
                  </span>
                </label>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text text-accent font-medium">
                    {t("promotions.coins.field_validity")}
                  </span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.value.validity_months ?? ""}
                  onInput={(e) =>
                    (formData.value.validity_months = nullableNumber(
                      (e.target as HTMLInputElement).value,
                    ))
                  }
                  class="input input-bordered focus:input-primary"
                  placeholder={t("promotions.coins.field_never_expires")}
                />
                <label class="label">
                  <span class="label-text-alt text-base-content/50">
                    {t("promotions.coins.field_validity_hint")}
                  </span>
                </label>
              </div>
            </div>

            <label class="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                checked={formData.value.enabled}
                onChange={(e) =>
                  (formData.value.enabled = (
                    e.target as HTMLInputElement
                  ).checked)
                }
                class="toggle toggle-primary toggle-sm"
              />
              <span class="label-text">
                {t("promotions.coins.field_enabled")}
              </span>
            </label>
          </section>
        </div>
      </RightSheet>
    );
  },
});
